import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { useCompanyAddCompany } from './useCompanyAddCompany';
import { mqMin } from 'utils/responsive';

const BgWrapper = styled.div`
    background-color: var(--bg-color-light);
    height: 100%;
    min-height: 100svh;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
`;

const Wrapper = styled.div`
    margin: 0 auto;
    padding: 40px 30px;
    border-radius: 12px;
    background-color: var(--white);
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: var(--font-medium);
    text-align: center;

    ${mqMin('NARROW')} {
        width: 600px;
    }
`;

const Title = styled.div`
    font-size: var(--font-x-large);
    color: var(--dark-blue);
    font-weight: 600;
    margin-bottom: 5px;
    text-align: center;
`;

const Description = styled.div`
    font-size: var(--font-medium);
    color: #626262;
    text-align: center;
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
`;

const CancelButton = styled.button`
    border: none;
    background: transparent;
    color: var(--dark-blue);
    font-size: 13px;
    cursor: pointer;
    text-decoration: underline;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

export const CompanyAddCompany: React.FC = () => {
    const {
        control,
        loading,
        errors,
        areaChoices,
        employeeChoices,
        revenueChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        showSnackbar,
        setShowSnackbar,
        submit,
    } = useCompanyAddCompany();
    const navigate = useNavigate();

    return (
        <BgWrapper>
            <Wrapper>
                <SnackBar message="企業を追加しました。" open={showSnackbar} setOpen={setShowSnackbar} />
                <Title>企業を追加する</Title>
                <Description>新しい企業のプロフィールをご入力ください</Description>
                <FlexContainer>
                    <FormLabel title="エリア" isRequired />
                    {areaChoices && areaChoices.length > 0 && (
                        <Controller
                            name="area"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormSelect
                                        {...field}
                                        options={areaChoices.map(([value, label]) => ({ value, label }))}
                                    />
                                    {errors.area && <FormError message="選択してください" />}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="従業員規模" isRequired />
                    {employeeChoices && employeeChoices.length > 0 && (
                        <Controller
                            name="number_of_employees"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormSelect
                                        {...field}
                                        options={employeeChoices.map(([value, label]) => ({ value, label }))}
                                    />
                                    {errors.number_of_employees && (
                                        <FormError message={errors.number_of_employees.message} />
                                    )}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="売上高" isRequired />
                    {revenueChoices && revenueChoices.length > 0 && (
                        <Controller
                            name="revenue_scale"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormSelect
                                        {...field}
                                        options={revenueChoices.map(([value, label]) => ({ value, label }))}
                                    />
                                    {errors.revenue_scale && <FormError message={errors.revenue_scale.message} />}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="営業利益" isRequired />
                    {profitLevelChoices && profitLevelChoices.length > 0 && (
                        <Controller
                            name="profit_level"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormSelect
                                        {...field}
                                        options={profitLevelChoices.map(([value, label]) => ({ value, label }))}
                                    />
                                    {errors.profit_level && <FormError message={errors.profit_level.message} />}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="純資産金額" isRequired />
                    {netAssetsLevelChoices && netAssetsLevelChoices.length > 0 && (
                        <Controller
                            name="net_assets_level"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormSelect
                                        {...field}
                                        options={netAssetsLevelChoices.map(([value, label]) => ({ value, label }))}
                                    />
                                    {errors.net_assets_level && <FormError message={errors.net_assets_level.message} />}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>

                <ButtonContainer>
                    <ActionButton
                        disabled={loading}
                        onClick={submit}
                        bgColor="dark-blue"
                        style={{ minWidth: '200px', maxWidth: '400px' }}
                    >
                        追加する
                    </ActionButton>
                    <CancelButton type="button" onClick={() => navigate('/company/dashboard/')}>
                        キャンセル
                    </CancelButton>
                </ButtonContainer>
            </Wrapper>
        </BgWrapper>
    );
};
